import { connectedListingContent } from "./connectedListing";
import { pendingPhotoImport, inventoryAttention } from "./inventoryPresentation";
import { stockReviewIsDue } from "./stock";
import { inventoryAgeDays } from "./inventoryFacts";
import type { InventoryItem, MarketplaceListing, ReOSDatabase, Sale } from "./types";

export type WorkspaceCollectionResource = "inventory" | "listings" | "orders";
export type WorkspaceCollectionRecord = InventoryItem | MarketplaceListing | Sale;

export interface WorkspaceCollectionQuery {
  cursor?: string;
  limit?: number;
  filters?: Record<string, string | undefined>;
}

export interface WorkspaceCollectionPage<T extends WorkspaceCollectionRecord = WorkspaceCollectionRecord> {
  resource: WorkspaceCollectionResource;
  records: T[];
  total: number;
  offset: number;
  limit: number;
  nextCursor?: string;
}

export class WorkspaceCollectionCursorError extends Error {
  constructor() {
    super("This result page is no longer valid. Return to the first page and try again.");
    this.name = "WorkspaceCollectionCursorError";
  }
}

export const WORKSPACE_COLLECTION_DEFAULT_LIMIT = 50;
export const WORKSPACE_COLLECTION_MAX_LIMIT = 100;

export function reconcileWorkspaceCollectionRecords<T extends WorkspaceCollectionRecord>(
  remoteRecords: T[],
  matchingLocalRecords: T[],
  authoritativeLocalRecords: T[]
) {
  const matchingLocalIDs = new Set(matchingLocalRecords.map((record) => record.id));
  const authoritativeLocalByID = new Map(authoritativeLocalRecords.map((record) => [record.id, record]));
  return remoteRecords.flatMap((remoteRecord) => {
    const localRecord = authoritativeLocalByID.get(remoteRecord.id);
    if (!localRecord || localRecord.updatedAt < remoteRecord.updatedAt) return [remoteRecord];
    return matchingLocalIDs.has(remoteRecord.id) ? [localRecord] : [];
  });
}

export function normalizedWorkspaceCollectionLimit(limit: number | undefined) {
  if (!Number.isFinite(limit)) return WORKSPACE_COLLECTION_DEFAULT_LIMIT;
  return Math.max(1, Math.min(Math.trunc(limit!), WORKSPACE_COLLECTION_MAX_LIMIT));
}

export function workspaceCollectionCursorOffset(resource: WorkspaceCollectionResource, query: WorkspaceCollectionQuery) {
  if (!query.cursor) return 0;
  const match = /^v1\.(\d+)\.([a-z0-9]+)$/.exec(query.cursor);
  if (!match || match[2] !== collectionSignature(resource, query.filters)) throw new WorkspaceCollectionCursorError();
  const offset = Number(match[1]);
  if (!Number.isSafeInteger(offset) || offset < 0) throw new WorkspaceCollectionCursorError();
  return offset;
}

export function workspaceCollectionCursorForOffset(resource: WorkspaceCollectionResource, query: WorkspaceCollectionQuery, offset: number) {
  return `v1.${offset}.${collectionSignature(resource, query.filters)}`;
}

export function paginateWorkspaceCollection<T extends WorkspaceCollectionRecord = WorkspaceCollectionRecord>(
  database: ReOSDatabase,
  workspaceID: string,
  resource: WorkspaceCollectionResource,
  query: WorkspaceCollectionQuery = {}
): WorkspaceCollectionPage<T> {
  const filters = normalizedFilters(query.filters);
  const limit = normalizedWorkspaceCollectionLimit(query.limit);
  const offset = workspaceCollectionCursorOffset(resource, query);
  const records = recordsForCollection(database, workspaceID, resource, filters);
  const pageRecords = records.slice(offset, offset + limit) as T[];
  return {
    resource,
    records: pageRecords,
    total: records.length,
    offset,
    limit,
    ...(offset + pageRecords.length < records.length
      ? { nextCursor: workspaceCollectionCursorForOffset(resource, query, offset + pageRecords.length) }
      : {})
  };
}

function recordsForCollection(database: ReOSDatabase, workspaceID: string, resource: WorkspaceCollectionResource, filters: Record<string, string>) {
  const query = filters.q?.toLowerCase() ?? "";
  if (resource === "inventory") {
    return database.inventoryItems
      .filter((item) => item.workspaceID === workspaceID && inventoryMatches(database, item, filters, query))
      .sort((left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt) || left.id.localeCompare(right.id));
  }
  if (resource === "listings") {
    return database.marketplaceListings
      .filter((listing) => listing.workspaceID === workspaceID && listingMatches(database, listing, filters, query))
      .sort((left, right) => listingPriority(left) - listingPriority(right) || Date.parse(right.updatedAt) - Date.parse(left.updatedAt) || left.id.localeCompare(right.id));
  }
  return database.sales
    .filter((sale) => sale.workspaceID === workspaceID && saleMatches(database, sale, filters, query))
    .sort((left, right) => salePriority(left) - salePriority(right) || Date.parse(right.soldAt) - Date.parse(left.soldAt) || left.id.localeCompare(right.id));
}

function inventoryMatches(database: ReOSDatabase, item: InventoryItem, filters: Record<string, string>, query: string) {
  const listings = database.marketplaceListings.filter((listing) => listing.workspaceID === item.workspaceID && listing.itemID === item.id);
  const searchable = [item.title, item.sku, item.brand, item.stockLocation, item.categoryID, item.color, item.size, item.material].filter(Boolean).join(" ").toLowerCase();
  const lifecycle = filters.lifecycle ?? "all";
  const lifecycleMatch = lifecycle === "all"
    || (lifecycle === "active" && !["sold", "archived"].includes(item.status))
    || (lifecycle === "in_stock" && item.quantity > 0 && !["sold", "archived"].includes(item.status))
    || (lifecycle === "out_of_stock" && item.quantity === 0 && !["sold", "archived"].includes(item.status))
    || (lifecycle === "ready" && item.status === "ready_to_list")
    || (lifecycle === "listed" && item.status === "listed" && listings.length > 0)
    || item.status === lifecycle;
  const attention = filters.attention ?? "all";
  const attentionMatch = attention === "all"
    || (attention === "needs_attention" && Boolean(inventoryAttention(item, database.itemPhotos.filter((photo) => photo.itemID === item.id).length, listings, database.channelPhotoJobs)))
    || (attention === "aging" && !["sold", "archived"].includes(item.status) && stockAgeDays(item) >= 60)
    || (attention === "review_due" && stockReviewIsDue(item))
    || (attention === "missing_photos" && !pendingPhotoImport(item, database.channelPhotoJobs) && !database.itemPhotos.some((photo) => photo.itemID === item.id));
  const age = stockAgeDays(item);
  const ageMatch = !filters.age || filters.age === "all"
    || (filters.age === "0-30" && age >= 0 && age <= 30)
    || (filters.age === "31-60" && age >= 31 && age <= 60)
    || (filters.age === "61-90" && age >= 61 && age <= 90)
    || (filters.age === "90-plus" && age > 90);
  return (!query || searchable.includes(query))
    && lifecycleMatch
    && attentionMatch
    && ageMatch
    && (!filters.age || filters.age === "all" || !["sold", "archived"].includes(item.status))
    && (!filters.category || filters.category === "all" || item.categoryID === filters.category)
    && (!filters.marketplace || filters.marketplace === "all" || listings.some((listing) => listing.marketplace === filters.marketplace));
}

function listingMatches(database: ReOSDatabase, listing: MarketplaceListing, filters: Record<string, string>, query: string) {
  const item = database.inventoryItems.find((candidate) => candidate.workspaceID === listing.workspaceID && candidate.id === listing.itemID);
  const searchable = [connectedListingContent(listing).title, listing.title, item?.sku, item?.brand, item?.stockLocation, listing.marketplace].filter(Boolean).join(" ").toLowerCase();
  const status = filters.status ?? "all";
  const statusMatch = status === "all" || listing.status === status || (status === "closed" && ["ended", "delisted", "archived"].includes(listing.status));
  const attention = filters.attention ?? "all";
  const attentionMatch = attention === "all"
    || (attention === "needs_action" && ["draft", "ready", "failed"].includes(listing.status))
    || (attention === "aging" && Boolean(item && !["sold", "archived"].includes(item.status) && stockAgeDays(item) >= 60))
    || (attention === "missing_photos" && !database.itemPhotos.some((photo) => photo.itemID === listing.itemID));
  return (!query || searchable.includes(query))
    && statusMatch
    && attentionMatch
    && (!filters.marketplace || filters.marketplace === "all" || listing.marketplace === filters.marketplace);
}

function saleMatches(database: ReOSDatabase, sale: Sale, filters: Record<string, string>, query: string) {
  const item = database.inventoryItems.find((candidate) => candidate.workspaceID === sale.workspaceID && candidate.id === sale.itemID);
  const searchable = [item?.title, item?.sku, sale.buyerName, sale.buyerPostcode, sale.marketplace].filter(Boolean).join(" ").toLowerCase();
  const status = filters.status ?? "all";
  const statusMatch = status === "all" || sale.status === status
    || (status === "open" && ["sold", "paid", "packed", "shipped"].includes(sale.status))
    || (status === "to_pack" && ["sold", "paid"].includes(sale.status))
    || (status === "to_ship" && sale.status === "packed")
    || (status === "in_transit" && sale.status === "shipped")
    || (status === "exceptions" && ["cancelled", "refunded"].includes(sale.status));
  return (!query || searchable.includes(query))
    && statusMatch
    && (!filters.marketplace || filters.marketplace === "all" || sale.marketplace === filters.marketplace);
}

function listingPriority(listing: MarketplaceListing) {
  return ({ failed: 0, ready: 1, draft: 2, publishing: 3, live: 4, sold: 5, ended: 6, delisted: 7, archived: 8 } as const)[listing.status];
}

function salePriority(sale: Sale) {
  return ({ sold: 0, paid: 1, packed: 2, shipped: 3, completed: 4, refunded: 5, cancelled: 6 } as const)[sale.status];
}

function stockAgeDays(item: InventoryItem) { return inventoryAgeDays(item) ?? -1; }


function normalizedFilters(filters: WorkspaceCollectionQuery["filters"]) {
  return Object.fromEntries(
    Object.entries(filters ?? {})
      .filter((entry): entry is [string, string] => Boolean(entry[1]?.trim()))
      .map(([key, value]) => [key, value.trim()])
      .sort(([left], [right]) => left.localeCompare(right))
  );
}

function collectionSignature(resource: WorkspaceCollectionResource, filters: WorkspaceCollectionQuery["filters"]) {
  const input = `${resource}:${JSON.stringify(normalizedFilters(filters))}`;
  let hash = 2166136261;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}
